import React from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import Footer from "./components/layout/Footer";
import { useAuth } from "./contexts/AuthContext";

export default function Layout() {
    const location = useLocation();
    const navigate = useNavigate();
    const { isAuthenticated, userRole, logout } = useAuth();
    
    const isActive = (path) => {
      return location.pathname === path;
    };
    
    const handleLogout = async () => {
      const success = await logout();
      if (success) {
        navigate('/');
      }
    };

    return (
      <div className="flex flex-col min-h-screen">
        {/* NAVBAR */}
        <nav className="px-6 py-4 flex items-center justify-between bg-white">
          <div>
            <img src="/asset/Logo/hireeLogo.png" alt="Logo" className="h-8 w-auto" />
          </div>
          <div className="hidden md:flex items-center space-x-8">
            <Link 
              to="/" 
              className={`hover:text-[#FF8C00] cursor-pointer ${isActive('/') ? 'font-bold' : ''}`}
            >
              Home
            </Link>
            <Link 
              to="/job-seekers" 
              className={`hover:text-[#FF8C00] cursor-pointer ${isActive('/job-seekers') ? 'font-bold' : ''}`}
            >
              For Job Seekers
            </Link>
            <Link 
              to="/businesses" 
              className={`hover:text-[#FF8C00] cursor-pointer ${isActive('/businesses') ? 'font-bold' : ''}`}
            >
              For Businesses
            </Link>
            <Link 
              to="/about" 
              className={`hover:text-[#FF8C00] cursor-pointer ${isActive('/about') ? 'font-bold' : ''}`}
            >
              About
            </Link>
            <Link 
              to="/blog" 
              className={`hover:text-[#FF8C00] cursor-pointer ${isActive('/blog') ? 'font-bold' : ''}`}
            >
              Blog
            </Link>
            {isAuthenticated ? (
              <div className="flex space-x-4 items-center">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="px-4 py-2 text-[#FF8C00] border border-[#FF8C00] rounded hover:bg-[#FF8C00] hover:text-white"
                >
                  Dashboard
                </button>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 bg-[#FF8C00] text-white rounded hover:bg-[#FF8C00]/90"
                >
                  Log Out
                </button>
              </div>
            ) : (
              <div className="flex space-x-4 items-center">
                <Link
                  to="/login"
                  className="px-4 py-2 text-[#FF8C00] border border-[#FF8C00] rounded hover:bg-[#FF8C00] hover:text-white"
                >
                  Log In
                </Link>
                <div className="relative group">
                  <button
                    className="px-4 py-2 bg-[#FF8C00] text-white rounded hover:bg-[#FF8C00]/90 flex items-center"
                  >
                    Sign Up
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                    <Link
                      to="/signup/regular"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-[#FF8C00]"
                    >
                      I am Looking for a Job
                    </Link>
                    <Link
                      to="/business/coming-soon"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-[#FF8C00]"
                    >
                      Hire Staff
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </nav>
  
        {/* Main Content */}
        <main className="flex-grow">
          <Outlet />
        </main>

        {/* Mobile App Download Banner */}
        <section className="bg-[#FF8C00] py-16 px-6">
          <div className="max-w-6xl mx-auto text-center text-white">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Find Jobs Near you on our mobile app, Download now!
            </h2>
            <div className="flex flex-col md:flex-row justify-center items-center gap-6 mt-8">
              <a 
                href="https://apps.apple.com/us/app/hiree/id6740465535"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-black rounded-xl flex items-center px-4 py-2 w-[160px] h-[48px] hover:opacity-90 transition-opacity"
              >
                <div className="mr-2">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.0343 12.6957C17.0136 9.69839 19.5227 8.37629 19.6287 8.31122C18.2123 6.21727 15.9776 5.94833 15.2161 5.92588C13.3119 5.72978 11.4733 7.0519 10.5028 7.0519C9.53227 7.0519 8.04543 5.9483 6.42728 5.98308C4.36526 6.01786 2.45102 7.15809 1.39649 8.96368C-0.818056 12.6284 0.846415 18.0231 2.98347 20.9756C4.04894 22.4227 5.30388 24.0429 6.9458 23.9813C8.53144 23.9198 9.14181 22.944 11.0685 22.944C12.9952 22.944 13.5609 23.9813 15.2272 23.9465C16.9379 23.9198 18.0146 22.4669 19.0355 21.0123C20.2683 19.3347 20.7566 17.6841 20.7774 17.6225C20.7359 17.6071 17.0551 16.2087 17.0343 12.6957Z" fill="white"/>
                    <path d="M14.1086 3.85953C14.9586 2.80812 15.519 1.36353 15.3507 0C14.1086 0.0519513 12.5783 0.823423 11.6937 1.84437C10.902 2.74918 10.2387 4.26198 10.4277 5.57363C11.8185 5.66116 13.2378 4.91069 14.1086 3.85953Z" fill="white"/>
                  </svg>
                </div>
                <div className="text-left">
                  <div className="text-xs text-white whitespace-nowrap">Download on the</div>
                  <div className="text-base font-semibold text-white">App Store</div>
                </div>
              </a>

              <div className="bg-black rounded-xl flex items-center px-4 py-2 w-[160px] h-[48px] opacity-70">
                <div className="mr-2">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1.32925 0.298818C1.14844 0.488964 1.04785 0.751456 1.04785 1.10823V22.8922C1.04785 23.2489 1.14844 23.5114 1.32925 23.7016L1.38955 23.7616L13.3838 11.7673V11.7068V11.6463L1.38955 -0.34812L1.32925 -0.288117V0.298818Z" fill="white"/>
                    <path d="M17.4604 15.8438L13.3838 11.7672V11.7067V11.6462L17.4604 7.56958L17.5509 7.61974L22.3256 10.2675C23.6776 11.0405 23.6776 12.3726 22.3256 13.1456L17.5509 15.7934L17.4604 15.8438Z" fill="white"/>
                    <path d="M17.551 15.7934L13.3839 11.6465L1.32935 23.7014C1.78277 24.1489 2.48936 24.199 3.28577 23.7516L17.551 15.7934Z" fill="white"/>
                    <path d="M17.551 7.50708L3.28577 -0.45122C2.48936 -0.89861 1.78277 -0.848571 1.32935 0.598981L13.3839 11.6462L17.551 7.50708Z" fill="white"/>
                  </svg>
                </div>
                <div className="text-left">
                  <div className="text-xs text-white whitespace-nowrap">GET IT ON</div>
                  <div className="text-base font-semibold text-white">Google Play</div>
                  <div className="absolute text-[9px] text-white font-medium -mt-2 italic">Coming Soon</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <Footer />
      </div>
    );
  }